import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Receipt, Plus, LogOut, CheckCircle2 } from "lucide-react";

const money = (n) => (n == null ? "—" : "£" + Number(n).toLocaleString("en-GB", { minimumFractionDigits: Math.abs(n % 1) < 0.005 ? 0 : 2, maximumFractionDigits: 2 }));
const STATUS = {
    awaiting_response: { label: "Awaiting your acceptance", cls: "bg-amber-100 text-amber-800" },
    accepted: { label: "Accepted", cls: "bg-emerald-100 text-emerald-800" },
    declined: { label: "Declined", cls: "bg-rose-100 text-rose-700" },
    expired: { label: "Expired", cls: "bg-slate-200 text-slate-600" },
};

export default function MyQuotes() {
    const { user, logout } = useAuth();
    const nav = useNavigate();
    const [rows, setRows] = useState(null);
    useEffect(() => { api.get("/my/quotes").then((r) => setRows(r.data)).catch(() => setRows([])); }, []);
    const another = () => { localStorage.setItem("quote_prefill", JSON.stringify({ name: user?.name, email: user?.email })); nav("/quote"); };

    return (
        <div className="min-h-screen bg-[#f4f7fc]">
            <header className="h-12 bg-brand-ink text-white flex items-center px-6 gap-3">
                <Receipt className="w-5 h-5" />
                <div className="font-display font-bold text-sm">Qube <span className="text-white/60">Hub</span> · My Quotes</div>
                <div className="ml-auto flex items-center gap-3 text-xs">
                    <span className="text-white/70">{user?.name}</span>
                    <button data-testid="myq-logout" onClick={async () => { await logout(); nav("/login"); }} className="flex items-center gap-1 hover:bg-white/10 px-2 py-1 rounded-sm"><LogOut className="w-3.5 h-3.5" /> Sign out</button>
                </div>
            </header>
            <div className="max-w-3xl mx-auto px-6 py-10" data-testid="my-quotes">
                <div className="flex items-end justify-between mb-6">
                    <div>
                        <h1 className="font-display font-bold text-3xl tracking-tight">Your quotes</h1>
                        <p className="text-sm text-muted-foreground mt-1">Accept a quote to proceed, or request a new one anytime.</p>
                    </div>
                    <button data-testid="myq-another" onClick={another} className="flex items-center gap-1.5 bg-brand hover:bg-brand-hover text-white text-sm px-4 py-2.5 rounded-sm"><Plus className="w-4 h-4" /> Get another quote</button>
                </div>
                {rows === null ? <div className="text-sm text-muted-foreground">Loading…</div> : rows.length === 0 ? (
                    <div className="bg-white border border-border rounded-md p-8 text-center" data-testid="myq-empty">
                        <p className="text-sm text-muted-foreground mb-4">You don't have any quotes yet.</p>
                        <button onClick={another} className="bg-brand hover:bg-brand-hover text-white text-sm px-5 py-2.5 rounded-sm">Get a quote</button>
                    </div>
                ) : (
                    <div className="space-y-3">
                        {rows.map((r) => {
                            const s = STATUS[r.status] || { label: r.status, cls: "bg-slate-100 text-slate-700" };
                            const canAccept = r.status === "awaiting_response" && !r.needs_manual_quote;
                            return (
                                <div key={r.ref} data-testid={`myq-row-${r.ref}`} className="bg-white border border-border rounded-md p-5 flex items-center gap-4 flex-wrap">
                                    <div className="flex-1 min-w-0">
                                        <div className="font-num text-[11px] text-muted-foreground">{r.ref}</div>
                                        <div className="font-display font-bold">{r.estate}</div>
                                        <div className="text-xs text-muted-foreground">{r.service_label}</div>
                                    </div>
                                    <div className="text-right">
                                        <div className="font-num font-semibold">{r.needs_manual_quote ? "Being priced" : money(r.total)}</div>
                                        {!r.needs_manual_quote && <div className="text-[11px] text-muted-foreground">{money(r.gross)} inc VAT</div>}
                                    </div>
                                    <span className={`text-[11px] font-semibold px-2 py-0.5 rounded-sm ${s.cls}`}>{s.label}</span>
                                    {canAccept
                                        ? <Link to={`/quote/accept/${r.token}`} data-testid={`myq-accept-${r.ref}`} className="bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-semibold px-4 py-2 rounded-sm">Review & accept</Link>
                                        : r.status === "accepted" ? <span className="text-emerald-700 text-xs flex items-center gap-1"><CheckCircle2 className="w-3.5 h-3.5" /> Accepted</span>
                                            : <Link to={`/quote/accept/${r.token}`} className="text-xs text-brand underline">View</Link>}
                                </div>
                            );
                        })}
                    </div>
                )}
            </div>
        </div>
    );
}
